import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Dropdown, TableRow } from 'semantic-ui-react';
import { COLORS, Row, Separator } from '@cuemath/comps';
import { Button } from '@cuemath/web-comps';
import { trackEvent } from 'modules/analytics';

import {
  ColoredChip,
  CopyButton,
  Filters,
  Heading,
  Table,
  TableCell,
  TableInfoCell,
} from 'comps';
import PlusIcon from 'img/plus.svg';
import { openModal } from 'actions/modal';
import { DropdownItem, DropdownMenu, DropdownText } from 'comps/dropdown';
import { useStudentPaymentGetAll } from 'actions/studentsv2';
import {
  usePaymentFlowOverridesViewGet,
  invalidatePaymentFlowOverridesView,
} from 'actions/payment-flow-overrides';
import PaymentFlowOverridesPanel from './pfo-panel';
import { useParams } from 'react-router-dom';
import { getFormatedDate } from 'utils';
import { useAppContext } from '../../../app-context';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { MODAL_FORM } from 'constants/form';
import MODAL from 'constants/modal';
import { EVENT_TYPE } from 'constants/logger';

const StyledButton = styled(Button)`
  img {
    margin-right: 8px;
  }
`;

const DisabledDropdownText = styled(DropdownText)`
  && {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

function Payments({ actions, userId, studentDetail }) {
  const { setSidePanelView } = useAppContext();
  const { id } = useParams();
  const {
    data: payments,
    getAll: getStudentPayments,
    isProcessing,
  } = useStudentPaymentGetAll(id);

  const { data: pfoView, get: getPfoView } = usePaymentFlowOverridesViewGet(id);
  const isAdmissionsCase = !!pfoView?.is_admissions_case;

  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    getStudentPayments(id);
    getPfoView(id);
    trackEvent(EVENT_TYPE.PAYMENT_SCREEN_VIEW, {
      student_id: id,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const onEditPaymentSetup = () => {
    actions.openModal(
      MODAL_FORM.PAYMENT_FLOW_OVERRIDES,
      { size: 'custom', width: `720px`, height: MODAL.HEIGHT },
      {
        student: { id },
        studentDetail,
        viewData: pfoView,
        onSaved: () => {
          invalidatePaymentFlowOverridesView();
          getPfoView(id);
        },
      },
    );
  };

  const handleDropdown = () => {
    setIsOpen(prev => !prev);
    trackEvent(EVENT_TYPE.GENERATE_PAYMENT_LINK_CLICK, {
      student_id: id,
    });
  };

  const onAddOfflinePayment = () => {
    actions.openModal(
      MODAL_FORM.PAYMENT_DATA_UPLOAD_MODAL,
      {
        size: 'custom',
        width: `${Math.ceil((window.innerWidth * 50) / 100)}px`,
        height: MODAL.HEIGHT,
      },
      { student: { id } },
    );
  };

  const onCreatePaymentLink = () => {
    actions.openModal(
      MODAL_FORM.PAYMENT,
      {
        size: 'custom',
        width: `${Math.ceil((window.innerWidth * 50) / 100)}px`,
        height: MODAL.HEIGHT,
      },
      { student: { id } },
    );
  };

  const onCreateNewPaymentLink = () => {
    const confirmedPayment = payments?.some(item => item.state === 'CNF');
    actions.openModal(
      MODAL_FORM.NEW_PAYMENT,
      {
        size: 'custom',
        width: `720px`,
        height: MODAL.HEIGHT,
      },
      {
        student: { id },
        studentDetail,
        isFirstTimePayment: !confirmedPayment,
        // Admissions one-time links are offline-only; the modal restricts the
        // band list to India (the only market with offline plans).
        isAdmissionsCase,
      },
    );
  };

  const dropDownDefaultSize = {
    'margin-top': '-10px',
    'margin-bottom': '-10px',
  };

  const getFullStatusText = (type, isRefunded, isUpgradePayment) => {
    if (isRefunded) {
      return 'Refunded';
    }
    if (type === 'GEN') {
      if (isUpgradePayment) {
        return 'Upgrade-Gen';
      }
      return 'Generated';
    }
    if (type === 'PAID' || type === 'CNF') {
      if (isUpgradePayment) {
        return 'Upgraded';
      }
      return 'Confirmed';
    }
    if (type === 'CAN') {
      return 'Cancelled';
    }
  };

  return (
    <>
      <Row justifyContent="space-between" alignItems="center">
        <Heading>Payments Info</Heading>
        <Dropdown
          style={dropDownDefaultSize}
          icon={
            <StyledButton
              size="xsmall"
              width="224px"
              iconConfig={{ imgLink: PlusIcon }}
              label="Generate payment link"
            />
          }
          floating
          className="icon"
          onOpen={handleDropdown}
          onClose={handleDropdown}
          open={isOpen}
        >
          <DropdownMenu top={'none'} right={'1px'}>
            {/* One-time is always available. For admissions the backend hard-
                filters it to offline plans (HYBRID/CLASS_K5/CUECENTER) since we
                don't yet know online-vs-offline; online admissions pay on leap,
                so Subscription-link generation is off for them. Renewals keep
                both, all plans. */}
            <DropdownItem onClick={onCreateNewPaymentLink}>
              <DropdownText>One-time</DropdownText>
            </DropdownItem>
            {isAdmissionsCase ? (
              <DropdownItem>
                <DisabledDropdownText title="Online admissions pay on the leap platform (see the payment setup panel). Use One-time for offline-course plans.">
                  Subscription
                </DisabledDropdownText>
              </DropdownItem>
            ) : (
              <DropdownItem onClick={onCreatePaymentLink}>
                <DropdownText>Subscription</DropdownText>
              </DropdownItem>
            )}
            <DropdownItem>
              <DropdownText onClick={onAddOfflinePayment}>Offline</DropdownText>
            </DropdownItem>
          </DropdownMenu>
        </Dropdown>
      </Row>
      {isAdmissionsCase && (
        <>
          <Separator height={16} transparent />
          <PaymentFlowOverridesPanel
            viewData={pfoView}
            onEdit={onEditPaymentSetup}
          />
          <Separator height={20} transparent />
        </>
      )}
      <Filters filters={[]} />
      <Table
        headerCell={[
          { title: 'Paid on' },
          { title: 'amount' },
          { title: 'discount' },
          { title: 'tenure' },
          { title: 'product' },
          { title: 'plan' },
          { title: 'status' },
          { title: 'Invoice ID' },
        ]}
        infoWidth="32px"
        showLoader={isProcessing}
        dataLength={payments?.length}
      >
        {payments?.map(payment => {
          const {
            amount,
            courses,
            currency,
            discount,
            duration,
            id: paymentId,
            invoice_id: invoiceId,
            paid_on: paidOn,
            rack_rate: rackRate,
            state,
            is_refunded: isRefunded,
            is_upgrade_payment: isUpgradePayment,
            product_type_text: productTypeText,
          } = payment;
          const { enrollment_type: enrollmentType, class_ratio: classRatio } =
            (courses?.length && courses[0]) || {};
          const isEnrolled = !!enrollmentType;
          const haveClassRatio = !!classRatio;
          const enrollmentTypeText =
            isEnrolled && haveClassRatio
              ? `${enrollmentType} 1:${classRatio}`
              : isEnrolled && !classRatio
              ? enrollmentType
              : null;

          return (
            <TableRow key={paymentId}>
              <TableInfoCell
                onClick={() =>
                  setSidePanelView({
                    view: 'payment-info',
                    props: payment,
                    title: 'Payment Info',
                  })
                }
                width={1}
              />
              <TableCell width={2}>{paidOn ? getFormatedDate(paidOn) : '-'}</TableCell>
              <TableCell width={2}>
                {currency || '-'} {amount || '-'}
              </TableCell>
              <TableCell width={1}>
                {(discount && rackRate && Math.ceil((discount / rackRate) * 100)) || 0}%
              </TableCell>
              <TableCell width={2}>{duration ? `${duration} Months` : '-'}</TableCell>
              <TableCell width={2}>{productTypeText}</TableCell>
              <TableCell width={3}>
                {enrollmentTypeText ? (
                  <ColoredChip
                    color={COLORS.WHITE}
                    variant={enrollmentType.toLowerCase()}
                  >
                    {enrollmentTypeText}
                  </ColoredChip>
                ) : (
                  '-'
                )}
              </TableCell>
              <TableCell width={2}>
                <ColoredChip
                  color={state === 'GEN' && COLORS.WHITE}
                  variant={(isRefunded ? 'ref' : state && state.toLowerCase()) || ''}
                >
                  {state ? getFullStatusText(state, isRefunded, isUpgradePayment) : '-'}
                </ColoredChip>
              </TableCell>
              <TableCell width={3}>
                {invoiceId && (
                  <CopyButton
                    copyMessage="Copy Invoice Id"
                    text={invoiceId}
                    marginTop="0px"
                    customTextStyle
                    padding="4px 8px"
                  />
                )}
              </TableCell>
            </TableRow>
          );
        })}
      </Table>
    </>
  );
}

export default connect(null, dispatch => ({
  actions: bindActionCreators(
    {
      openModal,
    },
    dispatch,
  ),
}))(Payments);
